import Alpine from 'alpinejs';
import resize from '@alpinejs/resize';
import intersect from '@alpinejs/intersect';

Alpine.plugin(resize);
Alpine.plugin(intersect);

import Player from '@vimeo/player';

let player, videoUrl;

/**
 * Setting up a store for modals.
 */
document.addEventListener('alpine:init', () => {
	Alpine.store('navigation', {
		menuVisible: null,

		toggle(id = null) {
			this.menuVisible = id;
		},
	});

	Alpine.store('modal', {
		videoModal: false,

		init() {
			Alpine.effect(() => {
				document.body.classList.toggle('modal-open', this.videoModal);
			});
		},

		toggleVideo(source = null) {
			this.videoModal = !this.videoModal;
			const contentWrapper = document.querySelector('.modal--video .modal-content-wrapper');

			if (source !== null) {
				// Continue playing the video where we left of.
				if (videoUrl === source && source.includes('vimeo')) {
					player.play();
				}

				/**
				 * Destroy the iframe if the source is different from the previous one.
				 * Then build a new iframe element, embed it and create a new Player.
				 */
				if (videoUrl !== source) {
					/**
					 * Remove iframe from modal before creating a new one.
					 */
					if (player !== undefined) {
						player.destroy();
					} else {
						contentWrapper.innerHTML = '';
					}

					/**
					 * Create new iframe and add default attributes.
					 */
					const iframe = document.createElement('iframe');
					iframe.setAttribute('frameborder', '0');
					iframe.setAttribute('webkitallowfullscreen', '');
					iframe.setAttribute('mozallowfullscreen', '');
					iframe.setAttribute('allowfullscreen', '');
					iframe.setAttribute('allow', 'autoplay; fullscreen');
					iframe.classList.add('modal-iframe');

					// Add source.
					iframe.setAttribute('src', source);

					// Insert iframe in contentWrapper as a child element.
					contentWrapper.appendChild(iframe);

					// Initialize Player
					if (source.includes('vimeo')) {
						player = new Player(iframe);

						// Play video when the player is initialized and loaded.
						player.on('loaded', function () {
							player.play();
						});
					}
				}

				// The source is saved in videoUrl to use in if-statements.
				videoUrl = source;
			} else if (videoUrl.includes('vimeo')) {
				// Pause video when modal is closed.
				player.pause();
			} else {
				const iframe = contentWrapper.getElementsByTagName('iframe')[0];
				const iframeSrc = iframe.src;

				// Reset iframe src to stop the video, but keeping the iframe intact.
				// This prevents from loading the src again when re-opening.
				iframe.src = iframeSrc;
			}
		},
	});

	Alpine.store('homepageTitleInteraction', {
		title: null,
		titlePos: null,
		beginPos: null,
		endPos: null,

		scrolling(el) {
			this.title = el;
			this.beginPos = el.parentElement.offsetTop;
			this.endPos = el.parentElement.clientHeight;
			this.titlePos = el.offsetTop;

			const maxBlur = 5; // Maximum blur value.
			const minOpacity = 0.1; // Minimum opacity value.
			const scrollPixelsBegin = 300; // How long it should take to reach maxBlur value in pixels.
			const scrollPixelsEnd = 400; // How long it should take to reach 0 blur value in pixels.
			const elHeight = el.clientHeight;

			if (this.titlePos <= 0 || this.titlePos >= this.endPos - elHeight) {
				// Remove style attribute if the title is at the top or bottom of the container.
				this.title.removeAttribute('style');
			} else if (this.titlePos >= this.endPos - elHeight - scrollPixelsEnd) {
				// End of scroll.
				const blur = (maxBlur - ((this.titlePos - (this.endPos - elHeight - scrollPixelsEnd)) / scrollPixelsEnd) * maxBlur); // eslint-disable-line prettier/prettier
				const opacity = Math.min(1, minOpacity + ((this.titlePos - (this.endPos - elHeight - scrollPixelsEnd)) / scrollPixelsEnd)); // eslint-disable-line prettier/prettier

				this.title.style.filter = `blur(${blur}px)`;
				this.title.style.opacity = opacity;
				this.title.style.pointerEvents = 'none';
			} else if (this.titlePos > 0) {
				// Start of scroll.
				const blur = Math.min(maxBlur, this.titlePos / (scrollPixelsBegin / maxBlur));
				const opacity = Math.max(minOpacity, 1 - this.titlePos / (scrollPixelsBegin / 1));

				this.title.style.filter = `blur(${blur}px)`;
				this.title.style.opacity = opacity;
				this.title.style.pointerEvents = 'none';
			}
		},
	});
});

/**
 * The window.Alpine = Alpine bit is optional, but is nice to have for freedom and flexibility.
 * Like when tinkering with Alpine from the devtools for example.
 */
// window.Alpine = Alpine;

/**
 * Ensure that Alpine.start() is only called once per page.
 * Calling it more than once will result in multiple "instances" of Alpine running at the same time.
 */
Alpine.start();
